import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api, Faq } from '../../services/api'
import { ChevronLeft, Plus, Trash2, Edit2, X, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'

export default function FaqManager() {
  const { token } = useAuth()
  const [faqs, setFaqs] = useState<Faq[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})
  
  const [editForm, setEditForm] = useState({ question: '', answer: '', order: 0, isActive: true })
  const [newForm, setNewForm] = useState({ question: '', answer: '', order: 0 })

  useEffect(() => {
    if (token) {
      loadFaqs()
    }
  }, [token])

  const loadFaqs = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const data = await api.getAdminFaqs(token)
      console.log('FAQs carregadas:', data.length)
      setFaqs(data)
      // Expandir todos por padrão
      const expanded: Record<string, boolean> = {}
      data.forEach(f => { expanded[f.id] = true })
      setExpandedItems(expanded)
    } catch (error) {
      console.error('Erro ao carregar FAQs:', error)
      toast.error('Erro ao carregar FAQs')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const expandAll = () => {
    const expanded: Record<string, boolean> = {}
    faqs.forEach(f => { expanded[f.id] = true })
    setExpandedItems(expanded)
    toast.success('Todas as perguntas expandidas')
  }

  const collapseAll = () => {
    const expanded: Record<string, boolean> = {}
    faqs.forEach(f => { expanded[f.id] = false })
    setExpandedItems(expanded)
    toast.success('Todas as perguntas recolhidas')
  }

  const handleAdd = async () => {
    if (!newForm.question.trim() || !newForm.answer.trim()) {
      toast.error('Preencha pergunta e resposta')
      return
    }
    setSaving(true)
    try {
      await api.createFaq(token!, {
        question: newForm.question,
        answer: newForm.answer,
        order: newForm.order
      })
      toast.success('FAQ adicionada com sucesso!')
      setIsAdding(false)
      setNewForm({ question: '', answer: '', order: 0 })
      await loadFaqs()
      // Dispara evento para atualizar o frontend público
      window.dispatchEvent(new CustomEvent('faqs-updated'))
    } catch (error) {
      console.error('Erro ao adicionar:', error)
      toast.error('Erro ao adicionar FAQ')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    if (!editForm.question.trim() || !editForm.answer.trim()) {
      toast.error('Preencha pergunta e resposta')
      return
    }
    setSaving(true)
    try {
      await api.updateFaq(token!, id, {
        question: editForm.question,
        answer: editForm.answer,
        order: editForm.order,
        isActive: editForm.isActive
      })
      toast.success('FAQ atualizada com sucesso!')
      setEditingId(null)
      await loadFaqs()
      // Dispara evento para atualizar o frontend público
      window.dispatchEvent(new CustomEvent('faqs-updated'))
    } catch (error) {
      console.error('Erro ao atualizar:', error)
      toast.error('Erro ao atualizar FAQ')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, question: string) => {
    if (!confirm(`Tem certeza que deseja excluir "${question}"?`)) return
    try {
      await api.deleteFaq(token!, id)
      toast.success('FAQ removida com sucesso!')
      await loadFaqs()
      // Dispara evento para atualizar o frontend público
      window.dispatchEvent(new CustomEvent('faqs-updated'))
    } catch (error) {
      console.error('Erro ao deletar:', error)
      toast.error('Erro ao remover FAQ')
    }
  }

  const handleToggleActive = async (faq: Faq) => {
    try {
      await api.updateFaq(token!, faq.id, { isActive: !faq.isActive })
      await loadFaqs()
      // Dispara evento para atualizar o frontend público
      window.dispatchEvent(new CustomEvent('faqs-updated'))
    } catch (error) {
      console.error('Erro ao alterar status:', error)
      toast.error('Erro ao alterar status')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mx-auto"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div>
              <h1 className="text-xl font-serif font-bold text-gray-900">Gerenciar FAQ</h1>
              <p className="text-sm text-gray-500">Adicione, edite ou remova perguntas frequentes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-[#51A64D] hover:bg-green-50 rounded-lg transition-colors"
            >
              <ChevronDown size={16} />
              Expandir todos
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronUp size={16} />
              Recolher todos
            </button>
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm rounded-lg hover:bg-[#458f3f] transition-colors"
            >
              <Plus size={16} />
              Nova Pergunta
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto">
        {/* Modal de adicionar */}
        {isAdding && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-lg w-full">
              <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <h3 className="font-serif font-bold text-lg text-gray-900">Nova Pergunta Frequente</h3>
                <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pergunta</label>
                  <input
                    type="text"
                    value={newForm.question}
                    onChange={(e) => setNewForm(prev => ({ ...prev, question: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#51A64D] focus:outline-none"
                    placeholder="Digite a pergunta"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resposta</label>
                  <textarea
                    value={newForm.answer}
                    onChange={(e) => setNewForm(prev => ({ ...prev, answer: e.target.value }))}
                    rows={4}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#51A64D] focus:outline-none"
                    placeholder="Digite a resposta"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                  <input
                    type="number"
                    value={newForm.order}
                    onChange={(e) => setNewForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#51A64D] focus:outline-none"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-400 mt-1">Número menor = aparece primeiro</p>
                </div>
              </div>
              <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
                <button onClick={() => setIsAdding(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
                <button onClick={handleAdd} disabled={saving} className="px-4 py-2 text-sm bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f] disabled:opacity-50">
                  {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de FAQs */}
        <div className="space-y-3">
          {faqs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-100">
              Nenhuma pergunta cadastrada. Clique em "Nova Pergunta" para começar.
            </div>
          ) : (
            faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {editingId === faq.id ? (
                  <div className="p-5 space-y-3">
                    <input
                      type="text"
                      value={editForm.question}
                      onChange={(e) => setEditForm(prev => ({ ...prev, question: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#51A64D] focus:outline-none"
                    />
                    <textarea
                      value={editForm.answer}
                      onChange={(e) => setEditForm(prev => ({ ...prev, answer: e.target.value }))}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#51A64D] focus:outline-none"
                    />
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={editForm.order}
                        onChange={(e) => setEditForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                      />
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editForm.isActive}
                          onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                        />
                        <span className="text-sm">Ativo</span>
                      </label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditingId(null)} className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
                      <button onClick={() => handleUpdate(faq.id)} disabled={saving} className="px-3 py-1 text-sm bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f]">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => toggleExpand(faq.id)}
                    >
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          {!faq.isActive && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inativo</span>
                          )}
                          <span className="text-xs text-gray-400">Ordem: {faq.order}</span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleToggleActive(faq)}
                          className="px-2 py-1 text-xs text-[#51A64D] hover:underline"
                        >
                          {faq.isActive ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(faq.id)
                            setEditForm({ question: faq.question, answer: faq.answer, order: faq.order, isActive: faq.isActive })
                          }}
                          className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(faq.id, faq.question)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                        <div className={`transform transition-transform duration-200 ${expandedItems[faq.id] ? 'rotate-180' : ''}`}>
                          <ChevronDown size={20} className="text-gray-400" />
                        </div>
                      </div>
                    </div>
                    
                    {expandedItems[faq.id] && (
                      <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-700 whitespace-pre-wrap">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}